import { trackTransition } from '@/components/SwitchTrackModal/trackTransition';
import { usePatientTrack } from '@/hooks/usePatientTrack';
import { ORBIT_PROVIDER_TASK_GROUP_IDENTIFIER, PROVIDER_ROLE_CODING_MAP } from '@bariendo/lib/codings';
import { ProviderRole, Track } from '@bariendo/lib/constants';
import { OPEN_TASK_STATUSES } from '@bariendo/lib/coverage-verification-task';
import { VerificationTaskOutcome } from '@bariendo/lib/pn-consultation-script';
import { CreateClinicalTaskInput } from '@bariendo/lib/task-management';
import { createReference, getReferenceString, MedplumClient } from '@medplum/core';
import { Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useCallback, useRef } from 'react';
import { fetchBot } from '@/services/services';
import { switchPatientTrack } from './trackSwitch';

export interface CoverageVerificationTask {
  title: string;
  description: string;
}

/**
 * Whether the Care Coordinators already have this exact task open for the
 * patient. The note's record of a done step is autosaved, so it can be lost (a
 * closed tab, a failed save, a second tab); this keeps a retry from creating a
 * second task. Matched on the title (stored as the Task's description), the
 * details (stored as its note) and the Care Coordinator role, so a task for a
 * different match, e.g. a corrected administrator, is still created.
 */
async function hasOpenTask(medplum: MedplumClient, patient: Patient, task: CoverageVerificationTask): Promise<boolean> {
  const role = PROVIDER_ROLE_CODING_MAP[ProviderRole.PATIENT_COORDINATOR];
  const tasks = await medplum.searchResources(
    'Task',
    {
      subject: getReferenceString(patient),
      status: OPEN_TASK_STATUSES.join(','),
      // The value alone, as the access policies' criteria and the inbox search it.
      'group-identifier': ORBIT_PROVIDER_TASK_GROUP_IDENTIFIER.value,
      _count: 100,
    },
    { cache: 'no-cache' }
  );
  return tasks.some(
    (open) =>
      open.description === task.title &&
      (open.note?.[0]?.text ?? '') === task.description &&
      open.performerType?.some((type) =>
        type.coding?.some((coding) => coding.system === role.system && coding.code === role.code)
      )
  );
}

/** Whether running moves the patient first: not on the target, and not already moved there by this card. */
function mustSwitch(track: Track | undefined, target: Track | undefined, switchedTo: Track | undefined): boolean {
  return target !== undefined && track !== target && switchedTo !== target;
}

/**
 * A coverage section's next step (BAR-2921): move the patient to the coverage
 * track if they are not on it, then create the verification task for the Care
 * Coordinator role. Shared by the employer and insurance sections.
 *
 * - The switch goes through `switch-patient-track`, the same bot and the same
 *   acknowledgement rule as the Switch track dialog (`trackTransition`), and
 *   every reader of the track on the page re-reads it afterwards.
 * - The task goes through `create-clinical-task` to the role, never a person
 *   or a care team (it works with no CareTeam since BAR-2896), unless that
 *   task is already open for the patient.
 * - A switch that succeeds before the task fails is remembered, so a retry
 *   creates only the task and still reports the switch.
 */
export function useCoverageVerification(
  patient: Patient | undefined,
  target: Track | undefined
): {
  /** The patient's track, once read; undefined while loading or when none is recorded. */
  currentTrack: Track | undefined;
  trackLoading: boolean;
  /** Whether running moves the patient to `target` first. */
  switching: boolean;
  /** Whether the move needs the operator's acknowledgement first, as in the Switch track dialog. */
  needsAcknowledgement: boolean;
  /**
   * `beforeTask` runs once the patient is on the target track and before the
   * task is created: writes that need the track's records to exist, e.g. the
   * insurance Coverage the switch creates or adopts (BAR-2922). `moved` says
   * whether this hook moved the patient, on this run or an earlier one. It
   * runs again on a retry, so it must be safe to repeat.
   */
  run: (
    task: CoverageVerificationTask,
    acknowledged: boolean,
    beforeTask?: (moved: boolean) => Promise<void>
  ) => Promise<VerificationTaskOutcome>;
} {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const { track: currentTrack, loading: trackLoading } = usePatientTrack(patient?.id);
  /** The track this hook already moved the patient to, if any. */
  const switchedTo = useRef<Track | undefined>(undefined);

  const switching = mustSwitch(currentTrack, target, switchedTo.current);
  const needsAcknowledgement =
    switching && target !== undefined && trackTransition(currentTrack, target).needsAcknowledgement;

  const run = useCallback(
    async (
      task: CoverageVerificationTask,
      acknowledged: boolean,
      beforeTask?: (moved: boolean) => Promise<void>
    ): Promise<VerificationTaskOutcome> => {
      if (!patient) throw new Error('No patient');
      // Read here, not from the render: a retry after the task failed can run
      // before the re-read track re-renders, and must not switch twice.
      if (target !== undefined && mustSwitch(currentTrack, target, switchedTo.current)) {
        await switchPatientTrack(
          medplum,
          patient,
          profile,
          currentTrack,
          target,
          acknowledged,
          'useCoverageVerification'
        );
        switchedTo.current = target;
      }

      if (beforeTask) await beforeTask(target !== undefined && switchedTo.current === target);

      if (!(await hasOpenTask(medplum, patient, task))) {
        const taskInput: CreateClinicalTaskInput = {
          patient: createReference(patient),
          title: task.title,
          description: task.description,
          performer: ProviderRole.PATIENT_COORDINATOR,
        };
        await fetchBot(medplum, 'create-clinical-task', taskInput);
      }
      return switchedTo.current === target ? 'switched' : 'created';
    },
    [medplum, profile, patient, target, currentTrack]
  );

  return { currentTrack, trackLoading, switching, needsAcknowledgement, run };
}
